import type React from 'react'
import { use, useCallback, useEffect, useMemo } from 'react'
import type {
  I18nConfig,
  I18nProviderProps,
  ReactI18n,
  TopLevelKeys,
  UseTranslationReturn,
} from './types'
import type { InterpolationMetadata, PluralMetadata } from './builders'
import { type ReactPromise, ReactPromise as RP } from '@w/utils-fe'
import { createStore, useStore } from '@w/react-store'

// ============================================================================
// Translation Resolution
// ============================================================================

function getValueAtPath(obj: unknown, path: string): unknown {
  const keys = path.split('.')
  let current = obj
  for (const key of keys) {
    if (current && typeof current === 'object' && key in current) {
      current = (current as Record<string, unknown>)[key]
    } else {
      return undefined
    }
  }
  return current
}

function interpolate(template: string, params: Record<string, string>): string {
  return template.replaceAll(/\{\{(\w+)\}\}/gu, (_, key) => {
    // oxlint-disable-next-line typescript/no-unsafe-member-access -- key is a string from regex capture group, params is Record<string, string>
    return params[key] ?? `{{${key}}}`
  })
}

function selectPluralRule(
  count: number,
  rules: PluralMetadata['rules'],
  locale: string,
  options?: { ordinal?: boolean },
): string {
  const useOrdinal = options?.ordinal ?? false

  // Check for explicit zero rule first
  if (!useOrdinal && count === 0 && rules.zero !== undefined) {
    return interpolate(rules.zero, { count: String(count) })
  }

  const pluralRules = new Intl.PluralRules(locale, {
    type: useOrdinal ? 'ordinal' : 'cardinal',
  })
  const rule = pluralRules.select(count)

  const selected = rules[rule] ?? rules.other ?? '{{count}} items'

  return interpolate(selected, { count: String(count) })
}

function resolveTranslation(
  value: unknown,
  params: Record<string, string | number> | undefined,
  locale: string,
): string {
  if (typeof value === 'string') {
    return value
  }

  if (value && typeof value === 'object' && '_type' in value) {
    if (value._type === 'interpolation') {
      const meta = value as InterpolationMetadata<readonly string[]>
      return interpolate(
        meta.template,
        (params as Record<string, string>) ?? {},
      )
    }

    if (value._type === 'plural') {
      const meta = value as PluralMetadata
      const { count = 0, ordinal = false } =
        (params as { count?: number; ordinal?: boolean }) ?? {}
      return selectPluralRule(count, meta.rules, locale, {
        ordinal,
      })
    }
  }

  return String(value)
}

// ============================================================================
// Store
// ============================================================================

type I18nStoreValue<T> = {
  translations: T | null
  locale: string
}

type LoadedTranslation<T> = {
  locale: string
  translations: T
  usedFallback: boolean
}

// ============================================================================
// React Implementation
// ============================================================================

export function createReactI18n<T>(config: I18nConfig<T>): ReactI18n<T> {
  const storage =
    config.localStorage ??
    (typeof window === 'undefined' ? undefined : localStorage)

  // Translation cache for eager loading and Suspense
  const translationCache = new Map<string, ReactPromise<T>>()
  const localeLoadCache = new Map<string, ReactPromise<LoadedTranslation<T>>>()

  // Helper to get initial locale
  const getInitialLocale = (): string => {
    if (storage) {
      const stored = storage.getItem(config.localStorageKey)
      if (stored) return stored
    }
    if (
      typeof navigator !== 'undefined' &&
      typeof navigator.language === 'string'
    ) {
      const browserLocale = navigator.language.split('-')[0]
      if (browserLocale) return browserLocale
    }
    return config.fallbackLocale
  }

  const initialLocale = getInitialLocale()

  // React store for state management
  const i18nStore = createStore<I18nStoreValue<T>>({
    locale: initialLocale,
    translations: null,
  })

  // Global store for non-React access
  const store = {
    locale: initialLocale,
    translations: null as T | null,
    listeners: new Set<() => void>(),
  }

  const activateTranslation = (
    locale: string,
    translations: T,
    persist: boolean,
  ): void => {
    i18nStore.update({ locale, translations })
    store.locale = locale
    store.translations = translations
    if (persist && storage) {
      storage.setItem(config.localStorageKey, locale)
    }
    for (const listener of store.listeners) {
      listener()
    }
  }

  // Helper to load translation with caching
  const loadTranslationCached = (locale: string): ReactPromise<T> => {
    const existing = translationCache.get(locale)
    if (existing) return existing

    const promise = config.loadTranslation(locale)
    const reactPromise = RP.wrap(promise)

    translationCache.set(locale, reactPromise)
    reactPromise.catch(() => {
      if (translationCache.get(locale) === reactPromise) {
        translationCache.delete(locale)
      }
    })
    return reactPromise
  }

  const loadLocale = (locale: string): ReactPromise<LoadedTranslation<T>> => {
    const existing = localeLoadCache.get(locale)
    if (existing) return existing

    const translationPromise = loadTranslationCached(locale)
    const promise = (async (): Promise<LoadedTranslation<T>> => {
      try {
        const translations = await translationPromise
        return { locale, translations, usedFallback: false }
      } catch (requestedError) {
        if (locale === config.fallbackLocale) {
          throw requestedError
        }

        try {
          const translations = await loadTranslationCached(
            config.fallbackLocale,
          )
          if (store.locale === locale) {
            activateTranslation(config.fallbackLocale, translations, true)
          }
          return {
            locale: config.fallbackLocale,
            translations,
            usedFallback: true,
          }
        } catch (fallbackError) {
          // oxlint-disable-next-line eslint/preserve-caught-error -- AggregateError errors retains both requested and fallback failures; cause also retains fallback failure
          throw new AggregateError(
            [requestedError, fallbackError],
            `Failed to load requested locale "${locale}" and fallback locale "${config.fallbackLocale}"`,
            { cause: fallbackError },
          )
        }
      }
    })()
    const reactPromise = RP.wrap(promise)
    localeLoadCache.set(locale, reactPromise)
    const deleteLocaleLoad = (): boolean =>
      localeLoadCache.get(locale) === reactPromise &&
      localeLoadCache.delete(locale)
    // React retries suspended renders with this same promise after it resolves.
    // Failed requested locales remain retryable, including successful fallbacks.
    void reactPromise.then(loaded => loaded.usedFallback && deleteLocaleLoad(), deleteLocaleLoad)
    return reactPromise
  }

  // Eagerly load initial locale
  void loadLocale(initialLocale)

  // Pre-fulfills reactPromise to avoid act() warnings in tests
  const loadTranslations = async (locale?: string): Promise<T> => {
    const targetLocale = locale ?? store.locale ?? initialLocale
    const loaded = await loadLocale(targetLocale)

    if (
      loaded.usedFallback ||
      targetLocale === store.locale ||
      !store.translations
    ) {
      activateTranslation(
        loaded.locale,
        loaded.translations,
        loaded.usedFallback,
      )
    }
    return loaded.translations
  }

  function useTranslation(): UseTranslationReturn<T>
  function useTranslation<P extends TopLevelKeys<T>>(
    prefix: P,
  ): UseTranslationReturn<T, P>
  function useTranslation<P extends TopLevelKeys<T> | undefined>(
    prefix?: P,
  ): UseTranslationReturn<T, P> {
    const storeValue = useStore(i18nStore)
    let { translations, locale } = storeValue

    // Suspend if translations not loaded yet
    // This ensures pendingComponents using translations wait for i18n to load
    if (!translations) {
      const loaded = use(loadLocale(locale))
      translations = loaded.translations
      locale = loaded.locale
    }

    const setLocale = useCallback((newLocale: string) => {
      i18nStore.update((prev: I18nStoreValue<T>) => ({
        ...prev,
        locale: newLocale,
      }))
      store.locale = newLocale
      if (storage) {
        storage.setItem(config.localStorageKey, newLocale)
      }
      // Pre-load the new locale
      void loadLocale(newLocale)
      // Notify standalone listeners
      for (const listener of store.listeners) {
        listener()
      }
    }, [])

    const t = useMemo(() => {
      return (key: string, params?: Record<string, string | number>) => {
        if (!translations) {
          return key
        }

        // Build full path considering prefix
        const fullPath = prefix ? `${prefix}.${key}` : key

        const value = getValueAtPath(translations, fullPath)

        if (value === undefined) {
          return fullPath
        }

        return resolveTranslation(value, params, locale)
      }
    }, [translations, locale, prefix])

    return { t, locale, setLocale }
  }

  const I18nProvider: React.FC<I18nProviderProps> = ({ children }) => {
    const storeValue = useStore(i18nStore)
    const { locale } = storeValue

    // Use Suspense to load translations
    const loaded = use(loadLocale(locale))

    // Sync store with loaded translations
    useEffect(() => {
      activateTranslation(
        loaded.locale,
        loaded.translations,
        loaded.usedFallback,
      )
    }, [loaded.locale, loaded.translations, loaded.usedFallback])

    return children
  }

  const useLocale = (): string => {
    const storeValue = useStore(i18nStore)
    return storeValue.locale
  }

  const setLocale = (newLocale: string): void => {
    i18nStore.update((prev: I18nStoreValue<T>) => ({
      ...prev,
      locale: newLocale,
    }))
    store.locale = newLocale
    if (storage) {
      storage.setItem(config.localStorageKey, newLocale)
    }
    // Pre-load the new locale
    void loadLocale(newLocale)
    // Notify standalone listeners
    for (const listener of store.listeners) {
      listener()
    }
  }

  // Standalone translation function for non-React usage
  const t = ((key: string, params?: Record<string, string | number>) => {
    if (!store.translations) {
      return key
    }

    const value = getValueAtPath(store.translations, key)

    if (value === undefined) {
      return key
    }

    return resolveTranslation(value, params, store.locale)
  }) as ReactI18n<T>['t']

  // Standalone locale getter
  const getLocale = (): string => {
    return store.locale
  }

  return {
    useTranslation,
    I18nProvider,
    useLocale,
    setLocale,
    loadTranslations,
    t,
    getLocale,
  }
}
